<template>
  <div class="member-type-select">
    <el-card class="select-card">
      <template #header>
        <div class="card-header">
          <h2>选择会员类型</h2>
          <p>请根据您的需求选择合适的会员类型</p>
        </div>
      </template>

      <MemberTypeSelector v-model="selectedType" v-model:level="selectedLevel" @change="handleTypeChange" @permissions-change="handlePermissionsChange" />

      <div class="action-buttons">
        <el-button size="large" @click="goBack">
          返回
        </el-button>
        <el-button type="primary" size="large" :disabled="!selectedType" @click="handleNext">
          下一步
        </el-button>
      </div>
    </el-card>

    <!-- 会员信息确认弹窗 -->
    <el-dialog
      v-model="confirmDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      :show-close="false"
      custom-class="confirm-dialog"
    >
      <template #header>
        <div class="confirm-header">
          <div class="confirm-icon-wrapper">
            <el-icon :size="28" color="#fff"><Wallet /></el-icon>
          </div>
          <h3 class="confirm-title">确认订单信息</h3>
          <p class="confirm-subtitle">请核对以下会员信息，确认无误后进行支付</p>
        </div>
      </template>

      <div class="confirm-body">
        <div class="confirm-card">
          <div class="confirm-row">
            <span class="confirm-label">
              <el-icon><User /></el-icon>会员类型
            </span>
            <span class="confirm-value">{{ confirmTypeName }}</span>
          </div>
          <div class="confirm-row" v-if="selectedType === MEMBER_TYPE.INDIVIDUAL">
            <span class="confirm-label">
              <el-icon><Star /></el-icon>会员等级
            </span>
            <el-tag :type="selectedLevel === 2 ? 'warning' : 'info'" size="small" effect="plain">
              {{ confirmLevelName }}
            </el-tag>
          </div>
          <div class="confirm-row">
            <span class="confirm-label">
              <el-icon><Calendar /></el-icon>缴费年份
            </span>
            <span class="confirm-value">{{ currentYear }}年</span>
          </div>
        </div>

        <div class="confirm-permissions-card" v-if="permissions.length">
          <span class="permissions-title">
            <el-icon><Check /></el-icon>会员权益
          </span>
          <ul class="permissions-list">
            <li v-for="(item, index) in permissions" :key="index">
              <el-icon><Check /></el-icon>{{ item }}
            </li>
          </ul>
        </div>

        <div class="confirm-amount-card">
          <span class="amount-label">应付金额</span>
          <div class="amount-value" v-if="confirmFee !== null">
            <span class="amount-symbol">¥</span>
            <span class="amount-number">{{ confirmFee }}</span>
          </div>
          <div class="amount-value negotiate" v-else>
            面议
          </div>
        </div>
      </div>

      <template #footer>
        <div class="confirm-footer">
          <el-button @click="confirmDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="goToPayment">
            <el-icon style="margin-right: 6px"><CreditCard /></el-icon>去支付
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 支付方式选择弹窗 -->
    <el-dialog
      v-model="paymentDialogVisible"
      title="选择支付方式"
      :width="selectedPaymentMethod === 'wechat' ? '700px' : '400px'"
      :close-on-click-modal="false"
    >
      <div class="payment-container" :class="{ 'wechat-active': selectedPaymentMethod === 'wechat' }">
        <div class="payment-methods">
          <div
            class="payment-item"
            :class="{ active: selectedPaymentMethod === 'wechat' }"
            @click="selectPaymentMethod('wechat')"
          >
            <img src="@/assets/images/weixin.png" alt="微信" class="payment-icon" />
            <span class="payment-name">微信支付</span>
            <i class="el-icon-check check-icon" v-if="selectedPaymentMethod === 'wechat'"></i>
          </div>
          <div
            class="payment-item"
            :class="{ active: selectedPaymentMethod === 'alipay' }"
            @click="selectPaymentMethod('alipay')"
          >
            <img src="@/assets/images/alipay.png" alt="支付宝" class="payment-icon" />
            <span class="payment-name">支付宝支付</span>
            <i class="el-icon-check check-icon" v-if="selectedPaymentMethod === 'alipay'"></i>
          </div>
          <div class="alipay-tip" v-if="selectedPaymentMethod === 'alipay'">
            <el-icon><InfoFilled /></el-icon>
            <span>点击下方按钮将跳转至支付宝收银台完成支付</span>
          </div>
        </div>

        <div class="qrcode-section" v-if="selectedPaymentMethod === 'wechat'">
          <div class="qrcode-header">
            <img src="@/assets/images/weixin.png" alt="微信" class="payment-logo" />
            <span class="payment-title">微信扫码支付</span>
          </div>
          <div class="qrcode-wrapper">
            <div v-show="qrcodeUrl" ref="qrcodeRef" class="qrcode-canvas"></div>
            <div v-show="!qrcodeUrl" class="qrcode-loading">
              <i class="el-icon-loading"></i>
              <p>二维码生成中...</p>
            </div>
          </div>
          <div class="qrcode-tips">
            <p>请使用微信扫描二维码完成支付</p>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="paymentDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmPayment" :disabled="!selectedPaymentMethod">
            {{ selectedPaymentMethod === 'alipay' ? '前往支付宝支付' : '已完成支付' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import MemberTypeSelector from '@/components/MemberTypeSelector.vue'
import { MEMBER_TYPE, MEMBER_TYPE_NAMES, MEMBER_LEVEL_NAMES } from '@/utils/constants'
import { User, Star, Calendar, Wallet, CreditCard, Check, InfoFilled } from '@element-plus/icons-vue'
import api from '@/api/index'
import QRCode from 'qrcodejs2-fix'

export default {
  name: 'MemberTypeSelect',

  components: {
    MemberTypeSelector,
    User,
    Star,
    Calendar,
    Wallet,
    CreditCard,
    Check,
    InfoFilled
  },

  data() {
    return {
      MEMBER_TYPE,
      selectedType: null,
      selectedLevel: null,
      permissions: [],
      confirmDialogVisible: false,
      confirmFee: null,
      paymentDialogVisible: false,
      selectedPaymentMethod: null, // 不设置默认支付方式
      qrcodeUrl: '' // 支付二维码URL
    }
  },

  computed: {
    ...mapGetters('auth', ['memberInfo']),
    currentYear() {
      return new Date().getFullYear()
    },
    confirmTypeName() {
      return MEMBER_TYPE_NAMES[this.selectedType] || ''
    },
    confirmLevelName() {
      return MEMBER_LEVEL_NAMES[this.selectedLevel] || ''
    }
  },

  mounted() {
    // 如果已有会员信息，预选择
    if (this.memberInfo && this.memberInfo.member_type) {
      this.selectedType = this.memberInfo.member_type
      this.selectedLevel = this.memberInfo.member_level
    }
  },

  methods: {
    /**
     * 处理类型变化
     */
    handleTypeChange() {
      // 会员类型已选择
    },

    /**
     * 处理权益列表变化
     */
    handlePermissionsChange(list) {
      this.permissions = list
    },

    /**
     * 下一步 - 弹出会员信息确认弹窗
     */
    async handleNext() {
      if (!this.selectedType) {
        this.$message.warning('请选择会员类型')
        return
      }

      // 个人会员需要选择等级
      if (this.selectedType === MEMBER_TYPE.INDIVIDUAL && !this.selectedLevel) {
        this.$message.warning('请选择会员等级')
        return
      }

      // 获取费用信息
      this.confirmFee = null
      if (this.selectedType === MEMBER_TYPE.INDIVIDUAL) {
        try {
          const res = await api.getFeeConfig({
            member_type: this.selectedType,
            member_level: this.selectedLevel
          })
          if (res.code === 2000 && res.data && res.data.specific_config) {
            this.confirmFee = res.data.specific_config.fee
          }
        } catch (e) {
          console.error('获取会费配置失败:', e)
        }
      }

      // 打开确认弹窗
      this.confirmDialogVisible = true
    },

    /**
     * 去支付 - 关闭确认弹窗，打开支付方式弹窗
     */
    goToPayment() {
      this.confirmDialogVisible = false
      this.paymentDialogVisible = true
      this.selectedPaymentMethod = null
      this.qrcodeUrl = ''
    },

    /**
     * 选择支付方式
     */
    async selectPaymentMethod(method) {
      this.selectedPaymentMethod = method
      // 微信需要立即生成二维码，支付宝在点击按钮时再创建订单
      if (method === 'wechat') {
        await this.createPaymentOrder(method)
      }
    },

    /**
     * 创建支付订单
     */
    async createPaymentOrder(method) {
      try {
        this.qrcodeUrl = '' // 清空二维码
        
        const params = {
          payment_type: method === 'wechat' ? 1 : 2, // 1-微信 2-支付宝
          payment_method: method === 'alipay' ? 'page' : 'native', // 支付方式：支付宝用page，微信用native
          member_level: this.selectedLevel || 1, // 会员等级
          year: new Date().getFullYear(), // 当前年份
          return_url: 'https://sdxlxh.cn/payment-successful', // 支付完成跳转地址
          remark: `${this.selectedType === MEMBER_TYPE.INDIVIDUAL ? '个人' : '企业'}会员支付`,
          member_type:1
        }

        const res = await api.createPayment(params)
        
        if (res && res.code === 2000) {
          if (method === 'alipay') {
            // 支付宝：打开新窗口跳转到支付页面
            const payData = res.data.pay_data
            if (payData) {
              window.open(payData, '_blank')
              this.paymentDialogVisible = false // 关闭弹窗
            } else {
              this.$message.error('获取支付链接失败')
            }
          } else {
            // 微信：将 code_url 生成二维码
            const codeUrl = res.data.pay_data && res.data.pay_data.code_url
            if (codeUrl) {
              this.qrcodeUrl = codeUrl
              this.$nextTick(() => {
                this.generateQRCode(codeUrl)
              })
            } else {
              this.$message.error('获取支付二维码失败')
            }
          }
        } else {
          this.$message.error(res.msg || '创建支付订单失败')
        }
      } catch (error) {
        console.error('创建支付订单失败:', error)
        this.$message.error('创建支付订单失败，请稍后重试')
      }
    },

    /**
     * 生成二维码
     */
    generateQRCode(url) {
      const container = this.$refs.qrcodeRef
      if (!container) return
      // 清空旧二维码
      container.innerHTML = ''
      new QRCode(container, {
        text: url,
        width: 200,
        height: 200,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H
      })
    },

    /**
     * 确认支付
     */
    async confirmPayment() {
      if (!this.selectedPaymentMethod) {
        this.$message.warning('请选择支付方式')
        return
      }

      // 支付宝：点击按钮时创建订单并跳转
      if (this.selectedPaymentMethod === 'alipay') {
        await this.createPaymentOrder('alipay')
        return
      }

      // 微信：用户已完成支付，关闭弹窗
      this.paymentDialogVisible = false
    },

    /**
     * 返回
     */
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style lang="scss" scoped>
.member-type-select {
  min-height: 100vh;
  padding: 40px 20px;
  background-color: #f5f7fa;

  .select-card {
    max-width: 1400px;
    margin: 0 auto;

    .card-header {
      text-align: center;

      h2 {
        margin: 0 0 10px 0;
        font-size: 28px;
        color: #303133;
      }

      p {
        margin: 0;
        color: #909399;
        font-size: 16px;
      }
    }

    .action-buttons {
      display: flex;
      justify-content: center;
      gap: 20px;
      margin-top: 30px;
      padding-top: 30px;
      border-top: 1px solid #e4e7ed;
    }
  }
}

@media (max-width: 768px) {
  .member-type-select {
    padding: 20px 10px;

    .select-card {
      .card-header {
        h2 {
          font-size: 22px;
        }

        p {
          font-size: 14px;
        }
      }

      .action-buttons {
        flex-direction: column;

        .el-button {
          width: 100%;
        }
      }
    }
  }
}

.payment-container {
  min-height: 200px;

  &.wechat-active {
    display: flex;
    gap: 30px;
    min-height: 350px;
  }
}

.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 15px;

  .wechat-active & {
    flex: 0 0 200px;
  }

  .payment-item {
    display: flex;
    align-items: center;
    padding: 15px;
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;

    &:hover {
      border-color: #409eff;
      background-color: #f0f9ff;
    }

    &.active {
      border-color: #409eff;
      background-color: #ecf5ff;
    }

    .payment-icon {
      width: 32px;
      height: 32px;
      margin-right: 12px;
      object-fit: contain;
    }

    .payment-name {
      font-size: 15px;
      color: #303133;
      font-weight: 500;
      flex: 1;
    }

    .check-icon {
      font-size: 18px;
      color: #409eff;
    }
  }

  .alipay-tip {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 12px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 6px;
    font-size: 13px;
    color: #e6a23c;
    line-height: 1.5;

    .el-icon {
      margin-top: 2px;
      flex-shrink: 0;
    }
  }
}

.qrcode-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 30px;

  .qrcode-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .payment-logo {
      width: 28px;
      height: 28px;
      margin-right: 10px;
    }

    .payment-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  .qrcode-wrapper {
    width: 220px;
    height: 220px;
    background-color: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    padding: 10px;

    .qrcode-canvas {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .qrcode-loading {
      text-align: center;
      color: #909399;

      i {
        font-size: 32px;
        margin-bottom: 10px;
      }

      p {
        margin: 0;
        font-size: 14px;
      }
    }
  }

  .qrcode-tips {
    text-align: center;

    p {
      margin: 0;
      color: #606266;
      font-size: 14px;
      line-height: 1.6;
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.confirm-header {
  text-align: center;
  padding-bottom: 8px;

  .confirm-icon-wrapper {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px;
    background: linear-gradient(135deg, #409eff, #337ecc);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(64, 158, 255, 0.3);
  }

  .confirm-title {
    margin: 0 0 6px;
    font-size: 20px;
    font-weight: 600;
    color: #303133;
  }

  .confirm-subtitle {
    margin: 0;
    font-size: 13px;
    color: #a8abb2;
  }
}

.confirm-body {
  padding: 0 4px;

  .confirm-card {
    background: #f8f9fb;
    border-radius: 10px;
    padding: 6px 20px;
    margin-bottom: 16px;

    .confirm-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 0;

      & + .confirm-row {
        border-top: 1px dashed #e8e8e8;
      }

      .confirm-label {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #606266;
        font-size: 14px;

        .el-icon {
          color: #909399;
          font-size: 16px;
        }
      }

      .confirm-value {
        color: #303133;
        font-size: 15px;
        font-weight: 500;
      }
    }
  }

  .confirm-permissions-card {
    background: #f0f9eb;
    border: 1px solid #e1f3d8;
    border-radius: 10px;
    padding: 16px 20px;
    margin-bottom: 16px;

    .permissions-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      font-weight: 500;
      color: #606266;
      margin-bottom: 10px;

      .el-icon {
        color: #67c23a;
        font-size: 16px;
      }
    }

    .permissions-list {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 0;
        font-size: 13px;
        color: #606266;

        .el-icon {
          color: #67c23a;
          font-size: 14px;
        }
      }
    }
  }

  .confirm-amount-card {
    background: linear-gradient(135deg, #fff7f0, #fff0f0);
    border: 1px solid #fde2ce;
    border-radius: 10px;
    padding: 20px;
    text-align: center;

    .amount-label {
      font-size: 13px;
      color: #909399;
      display: block;
      margin-bottom: 8px;
    }

    .amount-value {
      display: flex;
      align-items: baseline;
      justify-content: center;
      color: #f56c6c;

      .amount-symbol {
        font-size: 18px;
        font-weight: 600;
        margin-right: 2px;
      }

      .amount-number {
        font-size: 36px;
        font-weight: 700;
        line-height: 1;
        font-family: 'DIN', 'Helvetica Neue', sans-serif;
      }

      &.negotiate {
        font-size: 24px;
        font-weight: 600;
        color: #e6a23c;
      }
    }
  }
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .el-button--primary {
    padding: 10px 32px;
  }
}

@media (max-width: 768px) {
  .payment-container {
    &.wechat-active {
      flex-direction: column;
      gap: 20px;
    }
  }

  .payment-methods {
    flex: none;
  }

  .qrcode-section {
    padding: 20px;

    .qrcode-wrapper {
      width: 180px;
      height: 180px;
    }
  }
}
</style>

<style lang="scss">
/* 确认弹窗圆角 - 需要非 scoped 才能作用于 el-dialog 外层 */
.confirm-dialog {
  border-radius: 16px;
  overflow: hidden;

  .el-dialog__header {
    padding: 28px 28px 0;
    margin-right: 0;
  }

  .el-dialog__body {
    padding: 16px 28px;
  }

  .el-dialog__footer {
    padding: 12px 28px 24px;
  }
}
</style>
